import { apiClient } from '../client'

export type StreamEventLevel =
  | 'info'
  | 'warn'
  | 'error'
  | 'debug'
  | 'stdout'
  | 'stderr'

export interface StreamEventDto {
  event_id: string
  run_id: string
  node_execution_id: string
  component_name: string
  /** ISO-8601 timestamp from the SDK clock at emit time. */
  emitted_at: string
  level: StreamEventLevel
  payload: unknown
}

export interface ListEventsResponse {
  events: StreamEventDto[]
  /** Cursor for the next page; pass back as `since`. Null if no more. */
  next_since: string | null
}

export interface CentrifugoTokenResponse {
  /** HS256 JWT signed with the shared Centrifugo HMAC secret. */
  token: string
  /** Public WSS URL (through APISIX) the FE should open. */
  url: string
  /** Token TTL in seconds; FE should refresh ~30s before expiry. */
  ttl_seconds: number
}

interface ListEventsParams {
  node_execution_id?: string
  since?: string
  limit?: number
}

/**
 * Run-event APIs (Phase 2 history + Phase 3 live token).
 *
 * - `list` paginates the durable run_event history; cursor via `since`.
 * - `centrifugoToken` mints a short-lived subscription JWT scoped to this
 *   run's per-node channels (`run:{runId}:node:*`).
 */
export const eventsApi = {
  list: async (
    runId: string,
    params: ListEventsParams = {},
  ): Promise<ListEventsResponse> => {
    const { data } = await apiClient.get<ListEventsResponse>(
      `/run/${runId}/events`,
      { params },
    )
    return data
  },

  centrifugoToken: async (
    runId: string,
    nodeExecutionId: string,
  ): Promise<CentrifugoTokenResponse> => {
    // Centrifugo v5's `channels` claim is exact-match — no wildcards. We
    // pass the specific node so the backend mints a token scoped to that
    // one channel. To watch a different node, the FE re-fetches.
    const { data } = await apiClient.get<CentrifugoTokenResponse>(
      `/run/${runId}/centrifugo-token`,
      { params: { node_execution_id: nodeExecutionId } },
    )
    return data
  },
}
